import { Badge, Box } from '@palmetto/palmetto-components';
import { Helmet } from 'react-helmet';
import { Link, NavLink, Route, Routes, useParams } from 'react-router-dom';

import { IncentiveStatus, IncentiveStatusLabels, IncentiveTypeLabels } from 'types';

import { useGetIncentiveQuery } from '../../services/incentives';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';
import { SkeletonBox } from '../SkeletonBox';

import { IncentiveHistory } from './IncentiveHistory';
import { IncentiveOverview } from './IncentiveOverview';

function getStatusBadgeVariant(status: IncentiveStatus): 'success' | 'default' {
  return status === IncentiveStatus.active ? 'success' : 'default';
}

export function IncentiveDetails() {
  const { key } = useParams<{ key: string }>();
  const { data: incentive, isFetching } = useGetIncentiveQuery({ key: key! });

  return (
    <>
      <Helmet>
        <title>{incentive?.name ?? 'Incentive'}</title>
      </Helmet>
      <MainContainer>
        <PageHeader
          eyebrow={<Link to="/admin/incentives">Incentives</Link>}
          title={incentive?.name ?? ''}
          description={
            <Box direction="row" childGap="sm">
              <Badge
                variant="info"
                message={
                  !isFetching && incentive?.type ? (
                    IncentiveTypeLabels[incentive.type]
                  ) : (
                    <SkeletonBox height="14px" width="120px" />
                  )
                }
                size={{ base: 'md', desktop: 'lg' }}
              />
              {incentive?.status && (
                <Badge
                  variant={getStatusBadgeVariant(incentive.status)}
                  message={IncentiveStatusLabels[incentive.status]}
                  size={{ base: 'md', desktop: 'lg' }}
                />
              )}
            </Box>
          }
        />
        <Box direction="row" borderWidth="0 0 xs 0" borderColor="separator">
          <NavLink to={`/admin/incentives/${key}`} style={{ textDecoration: 'none', fontWeight: '500' }} end>
            {({ isActive }) => (
              <Box
                as="span"
                padding="md"
                className={isActive ? 'tab-active' : ''}
                hover={{ color: 'primary' }}
                style={{ transitionDuration: '0s' }}
              >
                Overview
              </Box>
            )}
          </NavLink>
          <NavLink to={`/admin/incentives/${key}/history`} style={{ textDecoration: 'none', fontWeight: '500' }}>
            {({ isActive }) => (
              <Box
                as="span"
                padding="md"
                className={isActive ? 'tab-active' : ''}
                hover={{ color: 'primary' }}
                style={{ transitionDuration: '0s' }}
              >
                History
              </Box>
            )}
          </NavLink>
        </Box>
        <Routes>
          <Route path="/" element={<IncentiveOverview />} />
          <Route path="/history" element={<IncentiveHistory />} />
        </Routes>
      </MainContainer>
    </>
  );
}
